/**
 * Canonical document type identifiers.
 * These are the keys the backend will use to reference specific document types.
 */
export const DOC = {
    US_PASSPORT:                     'US_PASSPORT',
    US_DRIVERS_LICENSE:              'US_DRIVERS_LICENSE',
    SSN_CARD:                        'SSN_CARD',
    BIRTH_CERTIFICATE:               'BIRTH_CERTIFICATE',
    NATURALIZATION_CERTIFICATE:      'NATURALIZATION_CERTIFICATE',
    PERMANENT_RESIDENT_CARD:         'PERMANENT_RESIDENT_CARD',
    FOREIGN_PASSPORT:                'FOREIGN_PASSPORT',
    I551_STAMP_VISA:                 'I551_STAMP_VISA',
    EXPIRED_PERMANENT_RESIDENT_CARD: 'EXPIRED_PERMANENT_RESIDENT_CARD',
    I797_EXTENSION:                  'I797_EXTENSION',
    EAD_I766:                        'EAD_I766',
    EXPIRED_EAD_I766:                'EXPIRED_EAD_I766',
    I797C_RECEIPT:                   'I797C_RECEIPT',
    I94:                             'I94',
    I797A_APPROVAL:                  'I797A_APPROVAL',
}

/**
 * Each flow is a sequential list of steps.
 * Within a step, `accepts` is an OR list — any one document satisfies that step.
 *
 * @typedef {{ purpose: string, label: string, accepts: string[] }} FlowStep
 * @typedef {{ flowId: string, userType: string, optionId: string, description: string, steps: FlowStep[] }} DocumentFlow
 */

/** @type {DocumentFlow[]} */
export const DOCUMENT_FLOWS = [
    // ── US Citizen ──────────────────────────────────────────────────────────
    {
        flowId: 'us_citizen__passport',
        userType: 'us_citizen',
        optionId: 'passport',
        description: 'US Citizen — Option 1 (One Step): US Passport or US Passport Card',
        steps: [
            {
                purpose: 'both_id_and_auth',
                label: 'US Passport or US Passport Card',
                accepts: [DOC.US_PASSPORT],
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_ssn',
        userType: 'us_citizen',
        optionId: 'dl_ssn_card',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Social Security Card",
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: [DOC.US_DRIVERS_LICENSE],
            },
            {
                purpose: 'employment_auth',
                label: 'Unrestricted "Social Security Card"',
                accepts: [DOC.SSN_CARD],
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_birth_cert',
        userType: 'us_citizen',
        optionId: 'dl_birth_cert',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Birth Certificate",
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: [DOC.US_DRIVERS_LICENSE],
            },
            {
                purpose: 'employment_auth',
                label: 'Original or Certified Birth Certificate',
                accepts: [DOC.BIRTH_CERTIFICATE],
            },
        ],
    },
    {
        flowId: 'us_citizen__dl_naturalization',
        userType: 'us_citizen',
        optionId: 'dl_naturalization',
        description: "US Citizen — Option 2 (Two Steps): Driver's License + Certificate of Naturalization",
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID",
                accepts: [DOC.US_DRIVERS_LICENSE],
            },
            {
                purpose: 'employment_auth',
                label: 'Certificate of Naturalization (Form N-550 or N-570)',
                accepts: [DOC.NATURALIZATION_CERTIFICATE],
            },
        ],
    },

    // ── Lawful Permanent Resident ────────────────────────────────────────────
    {
        flowId: 'lpr__i551',
        userType: 'lawful_permanent_resident',
        optionId: 'i551',
        description: 'Lawful Permanent Resident — Option 1: Permanent Resident Card (Form I-551)',
        steps: [
            {
                purpose: 'both_id_and_auth',
                label: 'Permanent Resident Card (Form I-551)',
                accepts: [DOC.PERMANENT_RESIDENT_CARD],
            },
        ],
    },
    {
        flowId: 'lpr__foreign_passport_i551_stamp',
        userType: 'lawful_permanent_resident',
        optionId: 'foreign_passport_i551_stamp',
        description: 'Lawful Permanent Resident — Option 2: Foreign Passport with I-551 Stamp',
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID or Foreign Passport",
                accepts: [DOC.US_DRIVERS_LICENSE, DOC.FOREIGN_PASSPORT],
            },
            {
                purpose: 'employment_auth',
                label: 'US Visa page containing the I-551 stamp',
                accepts: [DOC.I551_STAMP_VISA],
            },
        ],
    },
    {
        flowId: 'lpr__expired_i551_i797',
        userType: 'lawful_permanent_resident',
        optionId: 'i551_expired_i797',
        description: 'Lawful Permanent Resident — Option 3: Expired Permanent Resident Card with I-797 Extension',
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID or Foreign Passport",
                accepts: [DOC.US_DRIVERS_LICENSE, DOC.FOREIGN_PASSPORT],
            },
            {
                purpose: 'employment_auth_primary',
                label: 'Expired Permanent Resident Card (Form I-551)',
                accepts: [DOC.EXPIRED_PERMANENT_RESIDENT_CARD],
            },
            {
                purpose: 'employment_auth_extension',
                label: 'Official I-797 Extension Notice',
                accepts: [DOC.I797_EXTENSION],
            },
        ],
    },

    // ── EAD Holder ───────────────────────────────────────────────────────────
    {
        flowId: 'ead__i766',
        userType: 'ead_holder',
        optionId: 'ead_i766',
        description: 'EAD Holder — Option 1: Employment Authorization Document (Form I-766)',
        steps: [
            {
                purpose: 'both_id_and_auth',
                label: 'Employment Authorization Document (Form I-766)',
                accepts: [DOC.EAD_I766],
            },
        ],
    },
    {
        flowId: 'ead__expired_i766_i797c',
        userType: 'ead_holder',
        optionId: 'ead_expired_i797c',
        description: 'EAD Holder — Option 2: Expired EAD with I-797C Extension',
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID or Foreign Passport",
                accepts: [DOC.US_DRIVERS_LICENSE, DOC.FOREIGN_PASSPORT],
            },
            {
                purpose: 'employment_auth_primary',
                label: 'Expired EAD Card (Form I-766)',
                accepts: [DOC.EXPIRED_EAD_I766],
            },
            {
                purpose: 'employment_auth_extension',
                label: 'I-797C Receipt Notice',
                accepts: [DOC.I797C_RECEIPT],
            },
        ],
    },

    // ── Non-Immigrant Visa Holder ────────────────────────────────────────────
    {
        flowId: 'visa__i94',
        userType: 'non_immigrant_visa',
        optionId: 'foreign_passport_i94',
        description: 'Non-Immigrant Visa — Issued abroad or traveled since last US-based extension',
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID or Foreign Passport",
                accepts: [DOC.US_DRIVERS_LICENSE, DOC.FOREIGN_PASSPORT],
            },
            {
                purpose: 'employment_auth',
                label: 'Latest electronic I-94 Arrival/Departure Record',
                accepts: [DOC.I94],
            },
        ],
    },
    {
        flowId: 'visa__i797a',
        userType: 'non_immigrant_visa',
        optionId: 'foreign_passport_i797a',
        description: 'Non-Immigrant Visa — Issued in US and not traveled abroad since issuance',
        steps: [
            {
                purpose: 'id',
                label: "US State-issued Driver's License / ID or Foreign Passport",
                accepts: [DOC.US_DRIVERS_LICENSE, DOC.FOREIGN_PASSPORT],
            },
            {
                purpose: 'employment_auth',
                label: 'I-797A (Approval Notice) with attached I-94 section',
                accepts: [DOC.I797A_APPROVAL],
            },
        ],
    },
]

/**
 * Given the documents provided so far, determines which predefined flow(s) they
 * match and what documents are expected next.
 *
 * Documents are matched sequentially against each flow's steps.
 * Within a step, any one of the `accepts` document types satisfies that step (OR logic).
 *
 * @param {string[]} providedDocs - Ordered array of DOC.* values provided so far.
 * @returns {{
 *   status: 'in_progress' | 'complete' | 'unknown_flow',
 *   matchedFlows: Array<{
 *     flowId: string,
 *     userType: string,
 *     description: string,
 *     matchedSteps: number,
 *     remainingSteps: FlowStep[],
 *   }>,
 *   nextExpectedSteps: FlowStep[],
 * }}
 *
 * - `matchedFlows` lists every flow consistent with the documents provided so far.
 * - `nextExpectedSteps` is the deduplicated union of the first remaining step across
 *    all matched in-progress flows — useful for telling the user what to upload next.
 * - When `status` is `'unknown_flow'`, no defined flow matches the provided documents.
 */
export function checkDocumentFlow(providedDocs = []) {
    const matchedFlows = []

    for (const flow of DOCUMENT_FLOWS) {
        let stepCursor = 0
        let isMatch = true

        for (const doc of providedDocs) {
            if (stepCursor >= flow.steps.length) {
                // More documents provided than this flow has steps
                isMatch = false
                break
            }
            if (flow.steps[stepCursor].accepts.includes(doc)) {
                stepCursor++
            } else {
                isMatch = false
                break
            }
        }

        if (isMatch) {
            matchedFlows.push({
                flowId: flow.flowId,
                userType: flow.userType,
                description: flow.description,
                matchedSteps: stepCursor,
                remainingSteps: flow.steps.slice(stepCursor),
            })
        }
    }

    if (matchedFlows.length === 0) {
        return { status: 'unknown_flow', matchedFlows: [], nextExpectedSteps: [] }
    }

    const isComplete = matchedFlows.some((m) => m.remainingSteps.length === 0)

    // Collect unique next steps across all still-in-progress matched flows.
    // Deduplication key: sorted accepts joined, so OR-equivalent steps merge.
    const nextExpectedSteps = []
    const seen = new Set()
    for (const match of matchedFlows) {
        if (match.remainingSteps.length === 0) continue
        const nextStep = match.remainingSteps[0]
        const key = [...nextStep.accepts].sort().join(',')
        if (!seen.has(key)) {
            seen.add(key)
            nextExpectedSteps.push(nextStep)
        }
    }

    return {
        status: isComplete ? 'complete' : 'in_progress',
        matchedFlows,
        nextExpectedSteps,
    }
}
